class HeaderNavDropdown {
  constructor() {
    this.triggers = document.querySelectorAll('[data-header-nav-trigger]');
    this.triggers.forEach((trigger) => this.initTrigger(trigger));
    document.addEventListener('click', this.handleDocumentClick.bind(this));
    document.addEventListener('keydown', this.handleDocumentKeydown.bind(this));
  }

  getPanel(trigger) {
    const panelId = trigger.getAttribute('aria-controls');
    if (panelId) {
      const panelById = document.getElementById(panelId);
      if (panelById) return panelById;
    }

    return trigger.closest('[data-header-nav-item]')?.querySelector('[data-header-nav-panel]') || null;
  }

  initTrigger(trigger) {
    const panel = this.getPanel(trigger);
    if (!panel) return;

    const navItem = trigger.closest('[data-header-nav-item]');

    this.syncPanelState(trigger, panel, false);

    trigger.addEventListener('click', (event) => {
      if (trigger.tagName === 'BUTTON') {
        event.preventDefault();
        this.toggle(trigger, panel);
      }
    });

    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.open(trigger, panel);
        this.focusFirstMenuItem(panel);
        return;
      }

      if (event.key === 'Escape' && this.isOpen(trigger)) {
        event.preventDefault();
        event.stopPropagation();
        this.close(trigger, panel);
        trigger.focus();
        return;
      }

      if (trigger.tagName === 'BUTTON' && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.toggle(trigger, panel);
      }
    });

    // Close other menus when tabbing between triggers; never open on focus alone.
    trigger.addEventListener('focus', () => {
      this.closeAll(trigger);
    });

    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close(trigger, panel);
        trigger.focus();
      }
    });

    navItem?.addEventListener('mouseenter', () => {
      // Respect an Escape dismissal: don't re-reveal on hover until the pointer
      // leaves and re-enters (WCAG 1.4.13 dismissible).
      if (navItem.hasAttribute('data-esc-dismissed')) return;
      panel.removeAttribute('hidden');
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
    });

    navItem?.addEventListener('mouseleave', (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && navItem.contains(next)) return;
      navItem.removeAttribute('data-esc-dismissed');
      if (!this.isOpen(trigger)) {
        panel.setAttribute('hidden', '');
        panel.inert = true;
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  }

  focusFirstMenuItem(panel) {
    panel.querySelector('a, button:not([data-header-nav-trigger])')?.focus();
  }

  isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  isVisible(trigger, panel) {
    return this.isOpen(trigger);
  }

  syncInertState(trigger, panel) {
    const isVisible = this.isVisible(trigger, panel);

    if (isVisible) {
      panel.removeAttribute('hidden');
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
    } else {
      panel.setAttribute('hidden', '');
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
    }
  }

  syncPanelState(trigger, panel, isOpen) {
    trigger.setAttribute('aria-expanded', String(isOpen));
    const navItem = trigger.closest('[data-header-nav-item]');

    if (isOpen) {
      navItem?.setAttribute('data-nav-open', 'true');
    } else {
      navItem?.removeAttribute('data-nav-open');
    }

    this.syncInertState(trigger, panel);
  }

  open(trigger, panel) {
    this.closeAll(trigger);
    this.syncPanelState(trigger, panel, true);
  }

  close(trigger, panel) {
    this.syncPanelState(trigger, panel, false);

    const navItem = trigger.closest('[data-header-nav-item]');
    if (navItem?.matches(':hover') && !navItem.hasAttribute('data-esc-dismissed')) {
      panel.removeAttribute('hidden');
      panel.removeAttribute('inert');
      panel.removeAttribute('aria-hidden');
    }
  }

  toggle(trigger, panel) {
    if (this.isOpen(trigger)) {
      this.close(trigger, panel);
    } else {
      this.open(trigger, panel);
    }
  }

  closeAll(exceptTrigger = null) {
    this.triggers.forEach((trigger) => {
      if (trigger === exceptTrigger) return;
      const panel = this.getPanel(trigger);
      if (panel) this.close(trigger, panel);
    });
  }

  getOpenTrigger() {
    return Array.from(this.triggers).find((trigger) => this.isOpen(trigger)) || null;
  }

  handleDocumentClick(event) {
    if (!event.target.closest('[data-header-nav-item]')) {
      this.closeAll();
    }
  }

  handleDocumentKeydown(event) {
    if (event.key !== 'Escape') return;

    let dismissedAny = false;

    // Dismiss a panel opened via click/keyboard and restore focus to its trigger.
    const openTrigger = this.getOpenTrigger();
    if (openTrigger) {
      const panel = this.getPanel(openTrigger);
      if (panel) {
        const navItem = openTrigger.closest('[data-header-nav-item]');
        navItem?.setAttribute('data-esc-dismissed', 'true');
        this.close(openTrigger, panel);
        openTrigger.focus();
        dismissedAny = true;
      }
    }

    // Dismiss panels revealed only via hover so pointer users can clear the
    // flyout with Escape without moving the cursor (WCAG 1.4.13).
    this.triggers.forEach((trigger) => {
      if (this.isOpen(trigger)) return;
      const panel = this.getPanel(trigger);
      if (!panel || panel.hasAttribute('hidden')) return;
      panel.setAttribute('hidden', '');
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
      trigger.closest('[data-header-nav-item]')?.setAttribute('data-esc-dismissed', 'true');
      dismissedAny = true;
    });

    if (dismissedAny) event.preventDefault();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-header-nav-trigger]')) {
    new HeaderNavDropdown();
  }
});
